import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { CategoryGateway } from '@/di-container/category/gateway/client';

import {
  GetMenusRequest,
  MiddleCategoryItem,
  SectionMenu,
} from '@remodeling/content';
import { HEADER_HEIGHT } from '@display-component/mobile';
import { HandleLinkArgs } from '@display-component/shared';

import useEffectOnce from '@/hooks/useEffectOnce';
import { HamburgerMenuProps } from '../HamburgerMenu';
import useIframe from '@/hooks/useIframe';
import use100vh from '@/hooks/use100vh';

export interface LargeCategory {
  categoryName: string;
  categoryNo: string;
  isHighLight: 'Y' | 'N';
}

export interface LeftMenuItem {
  isCategory: 'Y' | 'N';
  leftMenuName: string;
  leftMenuNo?: string;
  isHighLight?: 'Y' | 'N';
}

export interface RightMenuItem {
  rightMenuName: string;
  rightMenuNo?: string;
  rightMenuList?: MiddleCategoryItem[];
  displayType?: 'I' | 'T';
  linkUrl: string;
}

const CATEGORY_TAB = [
  { tabName: '홈퍼니싱', value: 'HOME_FURNISHING' },
  { tabName: '인테리어', value: 'INTERIOR' },
];

const CATEGORY_TAB_HEIGHT = 52;

const useHamburgerMenu = ({
  isOpen,
  isHeader,
  isIframe,
}: HamburgerMenuProps) => {
  const router = useRouter();
  // 인입 채널 쿼리 스트링 - interior : 인테리어, furnishing : 홈퍼니싱
  const channel =
    router.query?.channel === 'furnishing' ? 'HOME_FURNISHING' : 'INTERIOR';

  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>(channel);
  const [isTabChanged, setIsTabChanged] = useState<boolean>(false);

  const { getHamburgerMenus } = CategoryGateway();

  const fetcher = (req: GetMenusRequest) => getHamburgerMenus.exec(req);

  const { data: categoryApiResponseData } = useSWR(
    { typeValue: 'MOBILE', viewType: 'ALL' },
    fetcher,
  );

  const [leftMenuItem, setLeftMenuItem] = useState<LeftMenuItem[]>();
  const [rightMenuItem, setRightMenuItem] = useState<RightMenuItem[]>();
  const [selectedLeftItem, setSelectedLeftItem] = useState<number>(0);
  const [scrolledRightItem, setScrolledRightItem] = useState<number>(0);

  const [isMwebAOS, setIsMwebAOS] = useState(false);

  useEffect(() => {
    setIsMwebAOS(navigator.userAgent.includes('Android'));
  }, []);

  const rightMenuItemRefs = useRef<null[] | HTMLElement[]>([]);

  const sectionMenuList = useMemo<SectionMenu[] | undefined>(() => {
    return categoryApiResponseData?.code === 200
      ? categoryApiResponseData?.data?.sectionMenuList || []
      : undefined;
  }, [
    categoryApiResponseData?.code,
    categoryApiResponseData?.data?.sectionMenuList,
  ]);

  const mallSectionMenu = useMemo(() => {
    if (sectionMenuList) {
      return sectionMenuList.find((item) => item.mallType === 'MALL');
    } else {
      return null;
    }
  }, [sectionMenuList]);

  const remodelingSectionMenu = useMemo(() => {
    if (sectionMenuList) {
      return sectionMenuList.find((item) => item.mallType === 'REMODELING');
    } else {
      return null;
    }
  }, [sectionMenuList]);

  // 카테고리 리스트와 메뉴 리스트를 더해 데이터 재가공
  useEffect(() => {
    const categoryData =
      selectedTab === 'HOME_FURNISHING'
        ? mallSectionMenu
        : remodelingSectionMenu;

    const leftCategoryList: LeftMenuItem[] = categoryData
      ? categoryData.groupCategoryList.map(
          ({ categoryName, categoryNo, isHighLight }) => {
            return {
              isCategory: 'Y',
              leftMenuName: categoryName,
              leftMenuNo: categoryNo,
              isHighLight,
            };
          },
        )
      : [];

    const leftMenuList: LeftMenuItem[] = categoryData
      ? categoryData.menuList?.map(({ menuDataNm, isHighLight }) => {
          return {
            isCategory: 'N',
            leftMenuName: menuDataNm,
            isHighLight,
          };
        })
      : [];

    setLeftMenuItem([...leftCategoryList, ...leftMenuList]);

    const rightCategoryList: RightMenuItem[] = categoryData
      ? categoryData.groupCategoryList.map(
          ({
            categoryName,
            categoryNo,
            middleCategoryList,
            isHighLight,
            displayType,
            linkUrl,
          }) => {
            return {
              rightMenuName: categoryName,
              rightMenuNo: categoryNo,
              rightMenuList: middleCategoryList,
              displayType,
              isHighLight,
              linkUrl,
            };
          },
        )
      : [];

    const rightMenuList: RightMenuItem[] = categoryData
      ? categoryData.menuList.map(({ menuDataNm, appLinkInfoDto }) => {
          return {
            rightMenuName: menuDataNm,
            linkUrl: appLinkInfoDto.linkUrl,
          };
        })
      : [];
    setRightMenuItem([...rightCategoryList, ...rightMenuList]);
  }, [mallSectionMenu, remodelingSectionMenu, selectedTab]);

  useEffectOnce(() => {
    setIsMounted(true);
  });

  const { handleIframeAppLink, handleIfrmaeLinkUrl, isIframeLoaded } =
    useIframe();

  useEffect(() => {
    isIframeLoaded();
  }, [isIframeLoaded]);

  const { VH_100 } = use100vh();

  const containerHeight = useMemo(
    () =>
      `calc(${VH_100} - ${
        isHeader ? HEADER_HEIGHT : 0
      }px - ${CATEGORY_TAB_HEIGHT}px)`,
    [VH_100, isHeader],
  );

  const fixedTop = useMemo(
    () =>
      `${(isHeader ? HEADER_HEIGHT : 0) + (CATEGORY_TAB_HEIGHT || 0) + 1}px`,
    [isHeader],
  );

  // 좌측 메뉴, 카테고리 영역
  const leftMenuScrollRef = useRef<HTMLDivElement | null>(null);
  const leftMenuListRef = useRef<null[] | HTMLElement[]>([]);

  // 우측 메뉴, 카테고리 영역
  const [rightMenu, setRightMenu] = useState<HTMLDivElement | null>(null);
  const rightMenuRef = useCallback((el: HTMLDivElement) => {
    setRightMenu(el);
  }, []);
  const [rightMenuList, setRightMenuList] = useState<Element[]>([]);
  const rightMenuHeight = useMemo(() => {
    return rightMenu?.getBoundingClientRect().height;
  }, [rightMenu]);

  const rightMenuLastItem =
    rightMenuList[rightMenuList?.length - 1]?.parentElement;
  const rightMenuMarginBottom = useMemo(() => {
    if (!rightMenuHeight) return;

    if (!rightMenuLastItem) return;

    return rightMenuHeight - rightMenuLastItem?.offsetHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightMenuHeight, rightMenuLastItem]);

  // MutationObserver를 통해 렌더링 시점 차이 극복
  useEffect(() => {
    if (rightMenu) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const hamburgerRights =
              document.querySelectorAll('.hamburger-right');

            if (hamburgerRights) {
              setRightMenuList(Array.from(hamburgerRights));
            }
          }
        });
      });

      observer.observe(rightMenu, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [rightMenu]);

  const scrollToMax = useCallback(() => {
    if (leftMenuScrollRef.current !== null) {
      leftMenuScrollRef.current.scrollTop =
        leftMenuScrollRef.current?.scrollHeight;
    }
  }, []);

  const [isLeftItemSelected, setIsLeftItemSelected] = useState<boolean>(false);

  const scrollLeftItem = useCallback(
    (index: number) => {
      if (isLeftItemSelected) return;

      if (index === 0) {
        leftMenuListRef.current[index]?.scrollIntoView({
          block: 'nearest',
        });

        return;
      }

      if (leftMenuItem && index === leftMenuItem?.length - 1) {
        scrollToMax();

        return;
      }

      const leftMenuScrollRefRect =
        leftMenuScrollRef.current?.getBoundingClientRect();
      const leftPrevMenuRefRect =
        leftMenuListRef.current[index - 1]?.getBoundingClientRect();
      const leftNextMenuRefRect =
        leftMenuListRef.current[index + 1]?.getBoundingClientRect();

      /**
       * 현재 활성화된 대카테고리가 좌측 스크롤 영역에 노출되지 않는 경우 노출되도록 스크롤 처리한다.
       * 노출되지 않는 활성화된 대카테고리 위 혹은 아래에 카테고리가 하나 더 존재하는 경우,
       * 해당 카테고리가 같이 보이도록 스크롤 처리
       * 1. 스크롤 영역의 top이 리스트의 top보다 큰 경우 (스크롤을 위로 해야 영역에 노출)
       * 2. 스크롤 영역의 bottom이 리스트의 bottom보다 작은 경우 (스크롤을 아래로 해야 영역에 노출)
       */

      if (!leftMenuScrollRefRect) return;

      if (
        leftPrevMenuRefRect?.top &&
        leftMenuScrollRefRect?.top > leftPrevMenuRefRect?.top
      ) {
        leftMenuListRef.current[index - 1]?.scrollIntoView({
          block: 'nearest',
        });
      }

      if (
        leftNextMenuRefRect?.bottom &&
        leftMenuScrollRefRect?.bottom < leftNextMenuRefRect?.bottom
      ) {
        const shouldScrollMax =
          leftMenuItem?.length && leftMenuItem?.length - 2 === index;

        if (shouldScrollMax) {
          scrollToMax();
        } else {
          leftMenuListRef.current[index + 1]?.scrollIntoView({
            block: 'nearest',
          });
        }
      }
    },
    [isLeftItemSelected, leftMenuItem, scrollToMax],
  );

  const handleClickLeftMenuItem = useCallback(
    (index: number) => {
      scrollLeftItem(index);

      setIsLeftItemSelected(true);
      setSelectedLeftItem(index);
    },
    [scrollLeftItem],
  );

  const handleChangeTab = useCallback((value: string) => {
    setSelectedTab(value);
    setIsLeftItemSelected(true);
    setIsTabChanged(true);
    setSelectedLeftItem(0);
  }, []);

  useEffect(() => {
    scrollLeftItem(scrolledRightItem);

    if (selectedLeftItem === scrolledRightItem) setIsLeftItemSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrolledRightItem]);

  useEffect(() => {
    if (isLeftItemSelected) {
      rightMenuList[selectedLeftItem]?.scrollIntoView({
        behavior: `${isTabChanged ? 'auto' : 'smooth'}`,
      });
      setIsTabChanged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeftItemSelected, rightMenuList, selectedLeftItem]);

  // 옵저버 함수 추가
  useEffect(() => {
    if (!rightMenu || rightMenuList.length === 0 || !rightMenuHeight) return;

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const seq = entry.target.getAttribute('data-seq');

        if (entry.isIntersecting) {
          seq && setScrolledRightItem(parseInt(seq));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: rightMenu,
      threshold: 0.2,
      rootMargin: `0px 0px -${(rightMenuHeight || 300) - 42}px 0px`,
    });

    rightMenuList?.forEach((element) => {
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer?.disconnect();
    };
  }, [rightMenu, rightMenuHeight, rightMenuList]);

  const handleScroll = useCallback((e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // background scroll 방어 로직
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('scroll', handleScroll);
    } else {
      document.body.style.overflow = '';
      window.removeEventListener('scroll', handleScroll);
    }

    return () => {
      window?.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, handleScroll]);

  interface _HandleLinkArgs extends HandleLinkArgs {
    linkUrl: string;
  }

  const handleAppLink = (
    appLink?: HandleLinkArgs | undefined | _HandleLinkArgs,
  ) =>
    isIframe
      ? handleIframeAppLink(appLink as HandleLinkArgs)
      : router.push((appLink as _HandleLinkArgs)?.linkUrl);

  const handleLinkUrl = (link: string) => {
    isIframe ? handleIfrmaeLinkUrl(link) : router.push(link);
  };

  return {
    CATEGORY_TAB_HEIGHT,
    isMounted,
    leftMenuItem,
    leftMenuScrollRef,
    leftMenuListRef,
    setIsLeftItemSelected,
    selectedLeftItem,
    setSelectedLeftItem,
    rightMenuItem,
    rightMenuItemRefs,
    rightMenuRef,
    rightMenuMarginBottom,
    rightMenuList,
    containerHeight,
    handleAppLink,
    handleLinkUrl,
    scrollLeftItem,
    scrolledRightItem,
    isMwebAOS,
    VH_100,
    handleClickLeftMenuItem,
    fixedTop,
    selectedTab,
    handleChangeTab,
    CATEGORY_TAB,
  };
};

export default useHamburgerMenu;
